import React, { useState, useEffect } from 'react';
import {
  BarChart3, PieChart as PieChartIcon, TrendingUp, Users, Mail, Phone,
  MapPin, Calendar, RefreshCw, Loader2, Building, UserCheck, UserX,
  DollarSign, FolderOpen, Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Area, AreaChart
} from 'recharts';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const EstadisticasContactos = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await axios.get(`${API}/api/estadisticas/contactos`);
      setStats(res.data);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar las estadísticas', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">No se pudieron cargar las estadísticas</p>
        <Button onClick={() => loadStats()} className="mt-4">Reintentar</Button>
      </div>
    );
  }

  const { resumen, por_tipo, por_ciudad, por_provincia, evolucion_mensual, sin_expedientes, top_clientes_valor, top_clientes_expedientes } = stats;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-7 h-7 text-green-600" />
            Estadísticas de Contactos
          </h1>
          <p className="text-gray-600 mt-1">Análisis detallado de la cartera de contactos</p>
        </div>
        <Button variant="outline" onClick={() => loadStats(true)} disabled={refreshing}>
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Actualizar
        </Button>
      </div>

      {/* KPIs Principales */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-xs">Total</p>
                <p className="text-2xl font-bold">{resumen.total}</p>
              </div>
              <Users className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs">Con Email</p>
                <p className="text-2xl font-bold">{resumen.con_email}</p>
              </div>
              <Mail className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-xs">Con Teléfono</p>
                <p className="text-2xl font-bold">{resumen.con_telefono}</p>
              </div>
              <Phone className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-cyan-100 text-xs">Este Mes</p>
                <p className="text-2xl font-bold">{resumen.creados_este_mes}</p>
              </div>
              <Calendar className="w-8 h-8 text-cyan-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-xs">Este Año</p>
                <p className="text-2xl font-bold">{resumen.creados_este_anio}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100 text-xs">Sin Expediente</p>
                <p className="text-2xl font-bold">{sin_expedientes}</p>
              </div>
              <UserX className="w-8 h-8 text-red-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-xs">% Email</p>
                <p className="text-2xl font-bold">{resumen.tasa_email}%</p>
              </div>
              <UserCheck className="w-8 h-8 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tasa de Contactabilidad */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Contactos con Email</span>
                <span className="text-sm text-gray-500">{resumen.tasa_email}%</span>
              </div>
              <Progress value={resumen.tasa_email} className="h-3" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Contactos con Teléfono</span>
                <span className="text-sm text-gray-500">{resumen.tasa_telefono}%</span>
              </div>
              <Progress value={resumen.tasa_telefono} className="h-3" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gráficos principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Por Tipo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChartIcon className="w-5 h-5" />
              Distribución por Tipo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={por_tipo.filter(t => t.valor > 0)}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={100}
                  paddingAngle={3}
                  dataKey="valor"
                  label={({ nombre, valor }) => `${nombre}: ${valor}`}
                >
                  {por_tipo.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Por Ciudad */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Contactos por Ciudad (Top 10)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={por_ciudad} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="nombre" type="category" width={100} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="valor" fill="#10B981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Evolución Mensual */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Nuevos Contactos - Últimos 12 Meses
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={evolucion_mensual}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
              <YAxis />
              <Tooltip />
              <Area type="monotone" dataKey="nuevos" name="Nuevos Contactos" stroke="#10B981" fill="#10B981" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Clientes por Valor */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-600" />
              Top Clientes por Valor
            </CardTitle>
            <CardDescription>Mayor valor económico en expedientes</CardDescription>
          </CardHeader>
          <CardContent>
            {top_clientes_valor.length > 0 ? (
              <div className="space-y-3">
                {top_clientes_valor.map((cliente, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                        idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-gray-300'
                      }`}>
                        {idx + 1}
                      </div>
                      <span className="font-medium text-sm truncate max-w-[120px]">{cliente.nombre}</span>
                    </div>
                    <Badge className="bg-green-100 text-green-700">{formatCurrency(cliente.valor)}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">Sin datos disponibles</p>
            )}
          </CardContent>
        </Card>

        {/* Top Clientes por Expedientes */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-blue-600" />
              Top Clientes por Expedientes
            </CardTitle>
            <CardDescription>Mayor número de expedientes</CardDescription>
          </CardHeader>
          <CardContent>
            {top_clientes_expedientes.length > 0 ? (
              <div className="space-y-3">
                {top_clientes_expedientes.map((cliente, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                        idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-600' : 'bg-gray-300'
                      }`}>
                        {idx + 1}
                      </div>
                      <span className="font-medium text-sm truncate max-w-[120px]">{cliente.nombre}</span>
                    </div>
                    <Badge className="bg-blue-100 text-blue-700">{cliente.expedientes} exp.</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">Sin datos disponibles</p>
            )}
          </CardContent>
        </Card>

        {/* Por Provincia */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building className="w-4 h-4 text-purple-600" />
              Distribución por Provincia
            </CardTitle>
          </CardHeader>
          <CardContent>
            {por_provincia.length > 0 ? (
              <div className="space-y-2">
                {por_provincia.slice(0, 8).map((prov, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{prov.nombre}</span>
                    <Badge variant="secondary">{prov.valor}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm text-center py-4">Sin datos de provincia</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EstadisticasContactos;
